import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import type { AggregatedMetrics } from "@cowork/shared";
import { formatCost, formatTokens } from "../../lib/utils.js";

const REPORTING_BASE = import.meta.env["VITE_REPORTING_URL"] ?? "/api";

async function fetchMetrics(days = 7): Promise<AggregatedMetrics | null> {
  try {
    const from = new Date(
      Date.now() - days * 24 * 60 * 60 * 1000,
    ).toISOString();
    const to = new Date().toISOString();
    const res = await fetch(
      `${REPORTING_BASE}/metrics?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
    if (!res.ok) return null;
    return (await res.json()) as AggregatedMetrics;
  } catch {
    return null;
  }
}

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
}

function StatCard({ label, value, sub }: StatCardProps) {
  return (
    <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border)] p-4">
      <div className="text-xs text-[var(--text-secondary)] mb-1">{label}</div>
      <div className="text-2xl font-bold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-xs text-[var(--text-secondary)] mt-1">{sub}</div>}
    </div>
  );
}

export function AnalyticsDashboard() {
  const [metrics, setMetrics] = useState<AggregatedMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setLoading(true);
    fetchMetrics(days)
      .then(setMetrics)
      .finally(() => setLoading(false));
  }, [days]);

  const modelData = metrics
    ? Object.entries(metrics.byModel).map(([model, m]) => ({
        model: model.split("-").slice(0, 2).join("-"),
        requests: m.requests,
        costUsd: m.costUsd,
        avgLatencyMs: Math.round(m.avgLatencyMs),
      }))
    : [];

  const providerData = metrics
    ? Object.entries(metrics.byProvider).map(([provider, m]) => ({
        provider,
        requests: m.requests,
        costUsd: m.costUsd,
      }))
    : [];

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
        <h2 className="text-base font-semibold" style={{ color: "var(--text-primary)" }}>Analytics</h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-xs rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] text-[var(--text-primary)] px-2 py-1.5 outline-none"
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-[var(--text-secondary)]">
          Loading metrics…
        </div>
      ) : !metrics ? (
        <div className="flex-1 flex items-center justify-center text-[var(--text-secondary)]">
          <div className="text-center">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" className="mx-auto mb-3" style={{ color: "var(--text-tertiary)" }}>
              <rect x="4" y="20" width="6" height="12" rx="1.5" fill="currentColor" opacity="0.4" />
              <rect x="14" y="12" width="6" height="20" rx="1.5" fill="currentColor" opacity="0.6" />
              <rect x="24" y="6" width="6" height="26" rx="1.5" fill="currentColor" opacity="0.8" />
            </svg>
            <p className="text-sm">No data yet. Start chatting to see metrics.</p>
            <p className="text-xs mt-1 opacity-60">
              Make sure the reporting service is running on port 3002.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 p-6 space-y-6">
          {/* Summary stats */}
          <div className="grid grid-cols-4 gap-4">
            <StatCard
              label="Total Requests"
              value={String(metrics.totalRequests)}
              sub={`Last ${days} day${days === 1 ? "" : "s"}`}
            />
            <StatCard
              label="Total Tokens"
              value={formatTokens(metrics.totalTokens)}
            />
            <StatCard
              label="Total Cost"
              value={formatCost(metrics.totalCostUsd)}
            />
            <StatCard
              label="Avg Latency"
              value={`${Math.round(metrics.avgLatencyMs)}ms`}
            />
          </div>

          {/* Provider breakdown */}
          {providerData.length > 0 && (
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border)] p-4">
              <h3 className="text-sm font-medium mb-4">Requests by Provider</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={providerData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="provider"
                    tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--bg-tertiary)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      color: "var(--text-primary)",
                    }}
                  />
                  <Bar dataKey="requests" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Model comparison */}
          {modelData.length > 0 && (
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border)] p-4">
              <h3 className="text-sm font-medium mb-4">Cost by Model</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={modelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="model"
                    tick={{ fill: "var(--text-secondary)", fontSize: 10 }}
                    angle={-20}
                    textAnchor="end"
                    height={40}
                  />
                  <YAxis
                    tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                    tickFormatter={(v: number) => `$${v.toFixed(4)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--bg-tertiary)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      color: "var(--text-primary)",
                    }}
                    formatter={(v: number) => [formatCost(v), "Cost"]}
                  />
                  <Bar dataKey="costUsd" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top skills */}
          {metrics.topSkills.length > 0 && (
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border)] p-4">
              <h3 className="text-sm font-medium mb-3">Top Skills</h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[var(--text-secondary)] text-left">
                    <th className="pb-2 font-normal">Skill</th>
                    <th className="pb-2 font-normal text-right">Invocations</th>
                    <th className="pb-2 font-normal text-right">Success Rate</th>
                    <th className="pb-2 font-normal text-right">Avg Time</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.topSkills.map((s) => (
                    <tr key={s.skillName} className="border-t border-[var(--border)]">
                      <td className="py-2">{s.skillName}</td>
                      <td className="py-2 text-right">{s.invocations}</td>
                      <td className="py-2 text-right">
                        {(s.successRate * 100).toFixed(0)}%
                      </td>
                      <td className="py-2 text-right">
                        {Math.round(s.avgDurationMs)}ms
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
